import React, { useEffect, useState } from "react";

/** External Libraries */
import { MenuItem, Select } from "@libraries/mui/components";
import { UnfoldMore } from "@libraries/mui/icons";
import { SelectChangeEvent, Skeleton } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { atom, useAtom } from "jotai";
import _ from "lodash";
/** Assets */
import IdentityLogo from "@assets/TopconLogo/Identity_Name.svg";
import TechLogo from "@assets/TopconLogo/Tech_Name.svg";
import FleetLogo from "@assets/TopconLogo/Fleet_Name.svg";
import DispatchLogo from "@assets/TopconLogo/Dispatch_Name.svg";
import BILogo from "@assets/TopconLogo/BI_Name.svg";
import CustomerLogo from "@assets/TopconLogo/Customer_Name.svg";
import BatchLogo from "@assets/TopconLogo/Batch_Name.svg";
/** Organisms */
import { useTabsAtom } from "@organisms/PageTabs";
/** Pages */
import { userRightsTabsAtom, selectedTabAtom } from "@pages/UsersRights";
/** Store */
import {
  getMyModules,
  TMyModuleQueries
} from "src/store/graphql/groups/module/query.graphql";
import {
  ECommandResultStatus,
  ModuleType
} from "src/store/graphql/helpers/types.graphql";

/** Styles */
import * as Styled from "./styles";

export const selectedModuleIdAtom = atom<string | null>(null);

const SelectIdentityModule: React.FC = () => {
  // region Queries
  const [QueryGetAllowedModules] = useLazyQuery<TMyModuleQueries>(
    getMyModules([
      "id", "name", "alias", "active"
    ])
  );
  // endregion Queries

  // region Constants
  const moduleIcons = [
    {
      alias: "TOPCON_IDENTITY",
      icon: IdentityLogo
    },
    {
      alias: "TOPCON_TECH",
      icon: TechLogo
    },
    {
      alias: "TOPCON_FLEET",
      icon: FleetLogo
    },
    {
      alias: "TOPCON_DISPATCH",
      icon: DispatchLogo
    },
    {
      alias: "TOPCON_BI",
      icon: BILogo
    },
    {
      alias: "TOPCON_CUSTOMER",
      icon: CustomerLogo
    },
    {
      alias: "TOPCON_BATCH",
      icon: BatchLogo
    }
  ];
  // endregion Constants

  // region Hooks
  const [selectedModuleId, setSelectedModuleId] = useAtom(selectedModuleIdAtom);
  const { selectedTab, pageTabs, setPageTabs } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  const { addToast } = useToast();
  // endregion Hooks

  // region States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [displayedModules, setDisplayedModules] = useState<ModuleType[]>();
  const [selectedModule, setSelectedModule] = useState<ModuleType>();
  // endregion States

  // region Functions
  const getModuleIcon = (alias: string | undefined) => moduleIcons?.find((module) => module.alias === alias)?.icon;

  const getUserAllowedModules = async () => {
    try {
      const query = await QueryGetAllowedModules();

      const response = query.data?.module.getMyModules;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as ModuleType[];

      return response.result as ModuleType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as ModuleType[];
    }
  };
  // endregion Functions

  // region Handlers
  const handleChange = (event: SelectChangeEvent) => {
    const module = displayedModules?.find((module) => module.id === event.target.value);

    if (!module) return;

    setSelectedModule(module as ModuleType);
    setSelectedModuleId(module.id);
    setPageTabs(pageTabs.filter((_tab, index) => index === 0));
  };
  // endregion Handlers

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const modules = await getUserAllowedModules();

      // `getMyModules` already returns only the modules manageable in the
      // Rights screen (those with a permission structure). The `active` flag
      // is a separate, mutable concern and must not gate this list.
      const filteredModules = modules;

      if (_.isEmpty(filteredModules)) {
        setSelectedModuleId("");
        setIsLoading(false);

        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const moduleParam = searchParams.get("module");

      const selectedModule = filteredModules.find((module) => module.name === moduleParam) || filteredModules[0];

      setSelectedModuleId(selectedModule.id);
      setSelectedModule(selectedModule);
      setDisplayedModules(filteredModules);

      setIsLoading(false);
    })();
  }, []);

  return (
    <Styled.Container>
      {isLoading
        ? <Skeleton variant="rectangular" animation="wave" width={200} height={24} />
        : (
          <Select
            sx={{
              boxShadow: "none",
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                border: 0
              },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0
                },
              ".MuiSvgIcon-root.Mui-disabled": {
                display: "none"
              },
              ".MuiSelect-select": {
                padding: "0px",
                display: "flex",
                alignItems: "center"
              }
            }}
            IconComponent={UnfoldMore}
            value={selectedModuleId ?? ""}
            renderValue={() => (
              <Styled.ModuleIcon
                src={getModuleIcon(selectedModule?.alias) || ""}
                alt={selectedModule?.name}
              />
            )}
            onChange={handleChange}
            disabled={selectedTab !== 0}
          >
            {displayedModules && displayedModules.map((module) => (
              <MenuItem sx={{ marginBottom: "10px" }} key={module.id} value={module.id}>
                <Styled.ModuleIcon src={getModuleIcon(module?.alias) || ""} alt={module.name} />
              </MenuItem>
            ))}
          </Select>
        )}
    </Styled.Container>
  );
};

export default SelectIdentityModule;